import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const BLOCK: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07 } },
};

const ITEM: Variants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 0.77, 0.47, 0.97] } },
};

const viewport = { once: true, amount: 0.05 };

const parseInline = (text: string): React.ReactNode[] => {
  const parts: React.ReactNode[] = [];
  const regex = /\*\*([^*]+)\*\*|\*([^*]+)\*|\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g;
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    if (match[1] !== undefined) {
      parts.push(<strong key={match.index} style={{ fontWeight: 600, color: '#1D2F41' }}>{match[1]}</strong>);
    } else if (match[2] !== undefined) {
      parts.push(<em key={match.index} style={{ fontStyle: 'italic' }}>{match[2]}</em>);
    } else {
      parts.push(
        <a key={match.index} href={match[4]} target="_blank" rel="noopener noreferrer"
          style={{ color: '#2b6161', textDecoration: 'underline' }}>
          {match[3]}
        </a>
      );
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
};

const renderAnswer = (text: string): React.ReactNode => {
  const lines = text.split('\n');
  return (
    <>
      {lines.map((line, i) => {
        if (line === '') return <br key={i} />;
        if (line === '---') {
          return (
            <Box
              key={i}
              sx={{
                my: 1.5,
                borderTop: '1px dashed rgba(29, 47, 65, 0.2)',
              }}
            />
          );
        }
        const subtitleMatch = line.match(/^\*\*(.+)\*\*$/);
        if (subtitleMatch) {
          return (
            <span key={i} style={{ display: 'block', marginTop: i > 0 ? '10px' : 0, marginBottom: '2px', fontWeight: 600, color: '#1D2F41' }}>
              {subtitleMatch[1]}
            </span>
          );
        }
        const conclusionMatch = line.match(/^>>>\s*(.+)$/);
        if (conclusionMatch) {
          return (
            <Box
              key={i}
              sx={{
                position: 'relative',
                mt: 1.5,
                px: 2.5,
                py: 2,
                bgcolor: 'rgba(43, 97, 97, 0.08)',
              }}
            >
              {/* Top-left corner */}
              <Box
                aria-hidden
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: 32,
                  height: '1px',
                  bgcolor: '#2b6161',
                  pointerEvents: 'none',
                }}
              />
              <Box
                aria-hidden
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '1px',
                  height: 32,
                  bgcolor: '#2b6161',
                  pointerEvents: 'none',
                }}
              />
              {/* Bottom-right corner */}
              <Box
                aria-hidden
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  width: 32,
                  height: '1px',
                  bgcolor: '#2b6161',
                  pointerEvents: 'none',
                }}
              />
              <Box
                aria-hidden
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  width: '1px',
                  height: 32,
                  bgcolor: '#2b6161',
                  pointerEvents: 'none',
                }}
              />
              <Box
                component="span"
                sx={{
                  display: 'block',
                  fontSize: '0.9rem',
                  color: '#1D2F41',
                  lineHeight: 1.75,
                  fontFamily: '"Calibri", sans-serif',
                  fontWeight: 400,
                }}
              >
                {parseInline(conclusionMatch[1])}
              </Box>
            </Box>
          );
        }
        return <span key={i} style={{ display: 'block' }}>{parseInline(line)}</span>;
      })}
    </>
  );
};

interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onClick: () => void;
}

const FAQItem: React.FC<FAQItemProps> = ({ question, answer, isOpen, onClick }) => {
  return (
    <Box sx={{ overflow: 'hidden' }}>
      <Box
        onClick={onClick}
        sx={{
          py: 2.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          '&:hover .q-text': { color: '#2b6161' },
        }}
      >
        <Typography
          className="q-text"
          sx={{
            fontSize: { xs: '0.95rem', md: '1rem' },
            fontWeight: isOpen ? 500 : 400,
            color: isOpen ? '#1D2F41' : 'rgba(29, 47, 65, 0.75)',
            fontFamily: '"Segoe UI", sans-serif',
            transition: 'color 0.3s ease, font-weight 0.2s ease',
            pr: 3,
            lineHeight: 1.5,
          }}
        >
          {question}
        </Typography>
        <Box
          sx={{
            position: 'relative',
            width: '20px',
            height: '20px',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <motion.div
            style={{ position: 'absolute', width: '14px', height: '1px', backgroundColor: isOpen ? '#2b6161' : '#1D2F41' }}
            animate={{ backgroundColor: isOpen ? '#2b6161' : '#1D2F41' }}
            transition={{ duration: 0.3 }}
          />
          <motion.div
            initial={false}
            animate={{ rotate: isOpen ? 90 : 0, opacity: isOpen ? 0 : 1 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            style={{ position: 'absolute', width: '1px', height: '14px', backgroundColor: '#1D2F41' }}
          />
        </Box>
      </Box>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          >
            <Box sx={{ pb: 3, pr: { xs: 2.5, md: 5 }, pl: 2, pt: 2, bgcolor: '#EBEDF0' }}>
              <Typography
                sx={{
                  fontSize: '0.9rem',
                  color: 'rgba(29, 47, 65, 0.65)',
                  lineHeight: 1.8,
                  fontFamily: '"Calibri", sans-serif',
                  fontWeight: 300,
                }}
              >
                {renderAnswer(answer)}
              </Typography>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

const FAQ: React.FC = () => {
  const { t } = useTranslation();
  const faqItems = t('faq.items', { returnObjects: true }) as Array<{ question: string; answer: string }>;
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqItems.map(item => ({
        "@type": "Question",
        "name": item.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.answer.replace(/\*\*([^*]+)\*\*/g, '$1').replace(/\*([^*]+)\*/g, '$1'),
        },
      })),
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "faq-schema";
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { document.getElementById("faq-schema")?.remove(); };
  }, [faqItems]);

  const handleToggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const mid = Math.ceil(faqItems.length / 2);
  const leftItems = faqItems.slice(0, mid);
  const rightItems = faqItems.slice(mid);

  return (
    <Box component="section" sx={{ bgcolor: '#f8f9fb', position: 'relative' }}>

      <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 3, md: 6 }, py: { xs: 10, md: 14 } }}>

        {/* Title */}
        <motion.div initial="hidden" whileInView="visible" viewport={viewport} variants={BLOCK}>
          <Box sx={{ mb: { xs: 8, md: 10 } }}>
            <motion.div variants={ITEM} style={{ willChange: 'transform' }}>
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  color: 'rgba(29, 47, 65, 0.5)',
                  fontFamily: '"Calibri", sans-serif',
                  mb: 3,
                }}
              >
                {t('faq.label')}
              </Typography>
            </motion.div>
            <motion.div variants={ITEM} style={{ willChange: 'transform' }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2.5rem', md: '3rem' },
                  fontWeight: 300,
                  letterSpacing: '-0.03em',
                  lineHeight: 1.1,
                  color: '#1D2F41',
                  fontFamily: '"Segoe UI", sans-serif',
                }}
              >
                {t('faq.heading')}
              </Typography>
            </motion.div>
          </Box>
        </motion.div>

        {/* Two-column accordion */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'flex-start' }}>

          {/* Left column */}
          <Box sx={{ flex: 1 }}>
            <motion.div initial="hidden" whileInView="visible" viewport={viewport} variants={BLOCK}>
              {leftItems.map((item, i) => (
                <motion.div key={i} variants={ITEM} style={{ willChange: 'transform' }}>
                  <FAQItem
                    question={item.question}
                    answer={item.answer}
                    isOpen={openIndex === i}
                    onClick={() => handleToggle(i)}
                  />
                </motion.div>
              ))}
            </motion.div>
          </Box>

          {/* Column divider */}
          <Box sx={{ display: { xs: 'none', md: 'block' }, width: '1px', alignSelf: 'stretch', bgcolor: 'rgba(29, 47, 65, 0.1)', mx: 6, flexShrink: 0 }} />

          {/* Right column */}
          <Box sx={{ flex: 1 }}>
            <motion.div initial="hidden" whileInView="visible" viewport={viewport} variants={BLOCK}>
              {rightItems.map((item, i) => (
                <motion.div key={mid + i} variants={ITEM} style={{ willChange: 'transform' }}>
                  <FAQItem
                    question={item.question}
                    answer={item.answer}
                    isOpen={openIndex === mid + i}
                    onClick={() => handleToggle(mid + i)}
                  />
                </motion.div>
              ))}
            </motion.div>
          </Box>

        </Box>
      </Box>
    </Box>
  );
};

export default FAQ;
